/* eslint-disable react-hooks/rules-of-hooks */
import { useCallback, useEffect, useRef } from "react";
import { useStore } from "../store/Store";

export type TouchInteractions = {
  rotate: boolean;
  zoom: {
    enabled: boolean;
    min: number;
    max: number;
  };
  move: boolean;
};

export const useTouchInteractions = (
  canvasRef: React.RefObject<HTMLCanvasElement>,
  interactions: TouchInteractions = {
    rotate: false,
    zoom: { enabled: true, min: 0.1, max: 4 },
    move: false,
  }
) => {
  const mouseX = useRef(0);
  const mouseY = useRef(0);
  const mousePointCount = useRef(0);
  const midPointXPrev = useRef<number | undefined>(undefined);
  const midPointYPrev = useRef<number | undefined>(undefined);
  const distanceBetweenFingersPrev = useRef<number | undefined>(undefined);

  const selectedObject = useStore((store) => store.selectedObject);

  const rotateObject = useCallback(
    (touchPoint1X: number, touchPoint1Y: number) => {
      if (!selectedObject) return;
      // Distance moved with first finger since last touch
      let deltaX = touchPoint1X - mouseX.current,
        deltaY = touchPoint1Y - mouseY.current;
      selectedObject.rotation.y += deltaX / 100;
      selectedObject.rotation.x += deltaY / 100;
    },
    [selectedObject]
  );

  const scaleObject = useCallback(
    (
      touchPoint1X: number,
      touchPoint1Y: number,
      touchPoint2X: number,
      touchPoint2Y: number
    ) => {
      if (!selectedObject) return;

      const distanceBetweenFingersCurrent = Math.sqrt(
        Math.pow(touchPoint2X - touchPoint1X, 2) +
          Math.pow(touchPoint2Y - touchPoint1Y, 2)
      );
      if (distanceBetweenFingersPrev.current !== undefined) {
        const movement =
          distanceBetweenFingersPrev.current - distanceBetweenFingersCurrent;
        const scaleNumber = selectedObject.scale.x + (movement * -1) / 100;
        // Make sure scale is within max and min
        const scale = Math.min(
          Math.max(scaleNumber, interactions.zoom.min),
          interactions.zoom.max
        );
        selectedObject.scale.set(scale, scale, scale);
      }
      distanceBetweenFingersPrev.current = distanceBetweenFingersCurrent;
    },
    [selectedObject, interactions]
  );

  const moveObject = useCallback(
    (
      touchPoint1X: number,
      touchPoint1Y: number,
      touchPoint2X: number,
      touchPoint2Y: number
    ) => {
      if (!selectedObject) return;
      // Calculated the midpoint between the two finger touchpoints
      const midPointX = (touchPoint2X + touchPoint1X) / 2;
      const midPointY = (touchPoint2Y + touchPoint1Y) / 2;

      if (
        midPointXPrev.current !== undefined &&
        midPointYPrev.current !== undefined
      ) {
        const distanceX = midPointX - midPointXPrev.current,
          distanceZ = midPointY - midPointYPrev.current;

        selectedObject.position.set(
          selectedObject.position.x + distanceX / 50,
          selectedObject.position.y,
          selectedObject.position.z + distanceZ / 50
        );
      }
      midPointXPrev.current = midPointX;
      midPointYPrev.current = midPointY;
    },
    [selectedObject]
  );

  const touchMove = useCallback(
    (evt: TouchEvent) => {
      const touchPoint1X = evt.targetTouches[0].clientX,
        touchPoint1Y = evt.targetTouches[0].clientY,
        touchPoint2X = evt.targetTouches[1]?.clientX,
        touchPoint2Y = evt.targetTouches[1]?.clientY;

      // If just one finger down rotate
      if (evt.targetTouches.length === 1) {
        if (mousePointCount.current !== 2) {
          // we had two finger inteaction previousely, now we have one finger. Delay the one finger interaction by a few ms
          // to avoid jumping of the object
          interactions.rotate && rotateObject(touchPoint1X, touchPoint1Y);
        }
      }

      // If two fingers down scale or move the object
      if (evt.targetTouches.length === 2) {
        const distanceBetweenFingersCurrent = Math.sqrt(
          Math.pow(touchPoint2X - touchPoint1X, 2) +
            Math.pow(touchPoint2Y - touchPoint1Y, 2)
        );
        if (distanceBetweenFingersCurrent > 100 && interactions.zoom) {
          scaleObject(touchPoint1X, touchPoint1Y, touchPoint2X, touchPoint2Y);
        } else {
          interactions.move &&
            moveObject(touchPoint1X, touchPoint1Y, touchPoint2X, touchPoint2Y);
        }
      }
      mouseX.current = evt.targetTouches[0].clientX;
      mouseY.current = evt.targetTouches[0].clientY;
      mousePointCount.current = evt.targetTouches.length;
      evt.stopPropagation();
    },
    [moveObject, rotateObject, scaleObject, interactions]
  );

  const touchStart = useCallback((evt: TouchEvent) => {
    mouseX.current = evt.targetTouches[0].clientX;
    mouseY.current = evt.targetTouches[0].clientY;

    evt.stopPropagation();
  }, []);

  const touchEnd = useCallback((evt: TouchEvent) => {
    distanceBetweenFingersPrev.current = undefined;
    midPointXPrev.current = undefined;
    midPointYPrev.current = undefined;

    console.log("touchStart event touches length", evt.targetTouches.length);
    evt.stopPropagation();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas?.addEventListener("touchstart", touchStart);
    canvas?.addEventListener("touchmove", touchMove);
    canvas?.addEventListener("touchend", touchEnd);
    return () => {
      canvas?.removeEventListener("touchstart", touchStart);
      canvas?.removeEventListener("touchmove", touchMove);
      canvas?.removeEventListener("touchend", touchEnd);
    };
  }, [canvasRef, selectedObject, touchEnd, touchMove, touchStart]);
};
